import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Plus, Gift, Trash2, Copy, Check } from 'lucide-react';
import api from '../lib/api';

export default function AdminPromosPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copied, setCopied] = useState(null);
  const [newPromo, setNewPromo] = useState({
    code: '',
    discount_type: 'subscription',
    discount_value: 1,
    max_uses: -1
  });

  useEffect(() => {
    if (!user?.admin_role) {
      navigate('/dashboard');
      return;
    }
    fetchPromos();
  }, [user, navigate]);

  const fetchPromos = async () => {
    try {
      const response = await api.get('/admin/promos');
      setPromos(response.data.promos);
    } catch (error) {
      console.error('Error fetching promos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromo = async () => {
    try {
      await api.post('/admin/promos', newPromo);
      setShowCreateModal(false);
      setNewPromo({ code: '', discount_type: 'subscription', discount_value: 1, max_uses: -1 });
      fetchPromos();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeletePromo = async (code) => {
    if (!confirm('Désactiver ce code promo ?')) return;
    try {
      await api.delete(`/admin/promos/${code}`);
      fetchPromos();
    } catch (error) {
      console.error('Error deleting promo:', error);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = 'SpenSpot';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPromo({ ...newPromo, code });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Codes Promo</h1>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Créer un code
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          </div>
        ) : promos.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucun code promo</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {promos.map((promo) => (
              <Card key={promo.code} className={!promo.is_active ? 'opacity-50' : ''}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Gift className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-lg">{promo.code}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyCode(promo.code)}>
                          {copied === promo.code ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {promo.discount_value} mois Premium • 
                        {promo.max_uses === -1 ? ' Illimité' : ` ${promo.current_uses}/${promo.max_uses} utilisations`}
                      </p>
                    </div>
                  </div>
                  {promo.is_active && (
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeletePromo(promo.code)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer un Code Promo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Code</label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newPromo.code}
                  onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                  placeholder="SpenSpotPROMO"
                  className="font-mono"
                />
                <Button variant="outline" onClick={generateCode}>Générer</Button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Mois Premium offerts</label>
              <Input
                type="number"
                min="1"
                value={newPromo.discount_value}
                onChange={(e) => setNewPromo({ ...newPromo, discount_value: parseInt(e.target.value) || 1 })}
                className="mt-2"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Utilisations max (-1 = illimité)</label>
              <Input
                type="number"
                value={newPromo.max_uses}
                onChange={(e) => setNewPromo({ ...newPromo, max_uses: parseInt(e.target.value) })}
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
            <Button onClick={handleCreatePromo} disabled={!newPromo.code}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
